import { Fragment, Children } from 'react';
import clsx from 'clsx';
import { Tab } from '@headlessui/react';

// Components
import Icon from '@/components/base/Icon';

// Types
import type { ReactNode } from 'react';

const borderStyles = {
    white: 'border border-white dark:border-icoDarkMode-anthracite',
    gray: 'border border-icoGray-300 dark:border-icoDarkMode-beton',
    none: ''
};

const backgroundStyles = {
    gray300: 'bg-icoGray-300 dark:bg-icoDarkMode-deepBlack',
    gray100: 'bg-icoGray-100 dark:bg-icoDarkMode-deepBlack'
};

const textSizeStyles = {
    xs: {
        text: 'text-xs',
        icon: 'text-sm'
    },
    sm: {
        text: 'text-sm',
        icon: 'text-base'
    }
};

export const TABS_VARIANTS = {
    DEFAULT: 'default',
    PANEL: 'panel'
} as const;

type TabGroupType = React.ComponentProps<(typeof Tab)['Group']>;

type TabType = {
    label: React.ReactNode;
    icon?: string;
    disabled?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    testId?: string;
};

type TabsProps = Pick<TabGroupType, 'onChange' | 'selectedIndex' | 'defaultIndex'> & {
    tabs: TabType[];
    border?: keyof typeof borderStyles;
    rounded?: boolean;
    children: ReactNode;
    variant?: (typeof TABS_VARIANTS)[keyof typeof TABS_VARIANTS];
    backgroundStyle?: keyof typeof backgroundStyles;
    textSize?: keyof typeof textSizeStyles;
    tabListClassName?: string;
    panelsClassName?: string;
    panelClassName?: string;
};

const Tabs = ({
    tabs = [],
    children,
    border = 'white',
    rounded = true,
    variant = TABS_VARIANTS.DEFAULT,
    textSize = 'sm',
    backgroundStyle = 'gray300',
    defaultIndex = 0,
    tabListClassName,
    panelsClassName,
    panelClassName,
    selectedIndex,
    onChange
}: TabsProps) => (
    <>
        {tabs?.length > 0 && (
            <Tab.Group
                defaultIndex={defaultIndex}
                selectedIndex={selectedIndex}
                onChange={onChange}>
                <Tab.List
                    className={clsx(
                        'scrollbar flex w-full overflow-x-auto overflow-y-hidden',
                        borderStyles[border],
                        tabListClassName,
                        {
                            'rounded-md': rounded,
                            'mb-8': variant === TABS_VARIANTS.DEFAULT,
                            [backgroundStyles[backgroundStyle]]: variant === TABS_VARIANTS.DEFAULT
                        }
                    )}>
                    {tabs.map((tab, index) => (
                        <Tab as={Fragment} key={`${index}-${tab.label.toString()}`}>
                            {({ selected }) => (
                                <button
                                    data-testid={tab?.testId}
                                    type="button"
                                    onClick={tab?.onClick}
                                    disabled={tab?.disabled}
                                    className={clsx(
                                        'tabs-tab group relative w-full px-5 font-GSemiBold transition duration-200 ease-in-out focus:z-10 focus:ring-inset',
                                        'disabled:text-icoGray-400 dark:disabled:text-icoDarkMode-wolf',
                                        textSizeStyles[textSize].text,
                                        {
                                            'rounded-md': rounded,
                                            'rounded-none': !rounded,
                                            'bg-white text-icoBlack dark:bg-icoDarkMode-anthracite dark:text-white':
                                                selected,
                                            'text-icoGray-400 dark:text-icoDarkMode-wolf': !selected
                                        }
                                    )}>
                                    <span
                                        className={clsx(
                                            'inline-block whitespace-nowrap py-3.5 after:absolute after:bottom-0 after:left-0 after:w-full',
                                            {
                                                'group-hover:text-icoBlack dark:group-hover:text-white':
                                                    !tab?.disabled,
                                                relative: variant === TABS_VARIANTS.DEFAULT,
                                                'selected after:block after:h-0.5 after:bg-icoBlue':
                                                    selected,
                                                'after:h-px after:bg-icoGray-300 dark:after:bg-icoDarkMode-deepBlack':
                                                    !selected,
                                                'after:hidden':
                                                    !selected && variant === TABS_VARIANTS.DEFAULT
                                            }
                                        )}>
                                        {tab.icon ? (
                                            <Icon
                                                icon={tab.icon}
                                                className={clsx(
                                                    'relative top-0.5 mr-2',
                                                    textSizeStyles[textSize].icon,
                                                    {
                                                        'text-icoBlue': selected,
                                                        'group-hover:text-icoBlue': !tab?.disabled
                                                    }
                                                )}
                                            />
                                        ) : null}
                                        {tab.label}
                                    </span>
                                </button>
                            )}
                        </Tab>
                    ))}
                </Tab.List>
                <Tab.Panels className={clsx('w-full', panelsClassName)}>
                    {Children.map(children, (panel) => {
                        return (
                            <Tab.Panel
                                className={clsx(
                                    'w-full focus:outline-none focus:ring-0',
                                    panelClassName
                                )}>
                                {panel}
                            </Tab.Panel>
                        );
                    })}
                </Tab.Panels>
            </Tab.Group>
        )}
    </>
);

export default Tabs;
